<template>
	<div class="Media" :class="classes">
		<div class="Media-content">
			<!-- @slot The main content goes here-->
			<slot name="content"></slot>
		</div>

		<div class="Media-caption">
			<!-- @slot This holds the caption-->
			<slot name="caption"></slot>
		</div>
	</div>
</template>

<script>
export default {
	components: {},
	data() {
		return {};
	},
	props: {
		/**
		 * align center
		 */
		center: {
			type: Boolean,
			default: false,
		},
		/**
		 * inline
		 */
		inline: {
			type: Boolean,
			default: false,
		},
		/**
		 * inline with flex
		 */
		inlineFlex: {
			type: Boolean,
			default: false,
		},
		/**
		 * extra padding
		 */
		loose: {
			type: Boolean,
			default: false,
		},
		/**
		 * horizontal rule above
		 */
		topRule: {
			type: Boolean,
			default: false,
		},
		/**
		 * caption overlayed at bottom of image
		 */
		overlap: {
			type: Boolean,
			default: false,
		},
		/**
		 * to be used inside a container with a set height
		 */
		contained: {
			type: Boolean,
			default: false,
		},
		/**
		 * has a drop shadow
		 */
		dropShadow: {
			type: Boolean,
			default: false,
		},
		/**
		 * for use as a hero layout
		 */
		hero: {
			type: Boolean,
			default: false,
		},
		/**
		 * no gap or margin between content and caption
		 */
		flush: {
			type: Boolean,
			default: false,
		},
		/**
		 * ratio of image width to caption width: can be 3to1, 2to1, 1to1, 1to2, 1to3
		 */
		ratio: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes() {
			const classes = {
				'Media--center': this.center,
				'Media--inline': this.inline || this.ratio,
				'Media--inlineFlex': this.inlineFlex,
				'Media--loose': this.loose,
				'Media--topRule': this.topRule,
				'Media--overlap': this.overlap,
				'Media--contained': this.contained,
				'Media--dropShadow': this.dropShadow,
				'Media--hero': this.hero,
				'Media--flush': this.flush,
			};
			if (this.ratio) {
				classes['Media--inline-' + this.ratio] = true;
			}
			return classes;
		},
	},
};
</script>

<style lang="scss">
/* --------------------------------
	Media component styles
-------------------------------- */

/* Media component - always stacked by default */
.Media {
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	@include stack-md;
	@supports (grid-area: x) {
		display: grid;
		grid-gap: $space-md;
		grid-template-columns: auto;
		grid-template-rows: auto auto;
		grid-template-areas:
			'content'
			'caption';
	}
	// Nested media needs margin removed
	.Media {
		margin: 0;
	}
}

/* Media content is an image, photo, video, etc. */
.Media-content {
	// background-color: pink; // for testing
	display: block;
	position: relative;
	flex: 0 1 auto;
	@supports (grid-area: x) {
		margin: 0;
		grid-area: content;
	}
}

/* Media caption is the text supporting the media */
.Media-caption {
	// background-color: lightblue; // for testing
	flex: 1 1 auto;
	@include inset-squish-md;
	@supports (grid-area: x) {
		padding: 0;
		grid-area: caption;
	}
	&--middle {
		align-self: center;
	}
}

/* ------------------------
	Variants for positioning
------------------------ */

/* Center variant - centers content and caption */
.Media--center {
	align-items: center;
	justify-content: center;
	justify-items: center;
	.Media-content {
		text-align: center;
	}
	.Media-caption {
		align-self: stretch;
		text-align: center;
	}
}

/* Flush variant - removes spacing between caption and content,
	useful for wrapping caption in a colored container */
.Media--flush {
	> .Media-caption {
		padding: 0;
	}
	@supports (grid-area: x) {
		grid-gap: 0;
	}
}

/* Loose variant - increases spacing between caption and content
	at large breakpoint */
.Media--loose {
	> .Media-caption {
		@include breakpoint(large) {
			@include inset-squish-lg;
			@supports (grid-area: x) {
				padding: 0;
			}
		}
	}
	@supports (grid-area: x) {
		@include breakpoint(large) {
			grid-gap: $space-lg;
		}
	}
}

/* Overlap variant - for captions that overlap the bottom of their content image */
.Media--overlap {
	.Media-caption {
		padding: 0;
	}
	@supports (grid-area: x) {
		display: grid; // override for Media inside media
		grid-gap: 0;
		> .Media-content {
			grid-row: row 1 / span 2; // content fills both rows
		}
		> .Media-caption {
			position: relative;
			grid-row: row 2;
			z-index: 10;
		}
	}
}

/* Hero overlap variant - for captions that float over a hero image */
.Media--heroOverlap {
	// Flex fallback layout
	position: relative;
	> .Media-content {
		img {
			@supports (mask-image: none) {
				mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0.5) 70%);
			}
			@supports not (mask-image: none) {
				opacity: 0.75; // Edge/IE fallback
			}
		}
		.ImageAspectRatio {
			.AspectRatioContent {
				background: transparent;
			}
		}
	}
	> .Media-caption {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		@include stack-md;
		max-width: 32rem;
		@include breakpoint(xlarge) {
			max-width: 34rem;
		}
	}
	// Grid layout
	@supports (display: grid) {
		grid-gap: 0;
		grid-template-columns: 1fr;
		grid-template-rows: 1fr;
		> .Media-content {
			grid-column: 1 / span 1;
			grid-row: 1 / span 1;
		}
		> .Media-caption {
			position: relative;
			top: unset;
			bottom: unset;
			left: unset;
			right: unset;
			transform: unset;
			grid-column: 1 / span 1;
			grid-row: 1 / span 1;
			z-index: 1;
			align-self: end;
			justify-self: center;
		}
		// On larger screens postion just below center
		@include breakpoint(xlarge) {
			grid-template-rows: 1fr 2fr;
			> .Media-content {
				grid-row: 1 / span 2;
			}
			> .Media-caption {
				grid-row: 2 / span 1;
				align-self: center;
				justify-self: center;
				margin: 0;
			}
		}
	}
}

/* Hero overlap variant - only medium and up, caption goes below content for small */
.Media--heroOverlapMask {
	@include breakpoint(medium) {
		> .Media-content {
			img {
				@supports (mask-image: none) {
					mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0.6) 70%);
				}
				@supports not (mask-image: none) {
					opacity: 0.75; // Edge/IE fallback
				}
			}
		}
	}
}

/* No content variant - when content in a looped series of media object is missing
	this class can be applied conditionally to close the empty space */
.Media--noContent {
	.Media-content {
		display: none;
	}
	.Media-caption {
		padding: 0;
	}
	@supports (grid-area: x) {
		grid-template-areas:
			'caption'
			'caption' !important;
		@include breakpoint(medium) {
			grid-template-areas: 'caption caption' !important;
		}
	}
}

/* Inline flex variant - goes inline when
	16rem of space is available on the right */
.Media--inlineFlex {
	flex-direction: row;
	flex-wrap: wrap;
	.Media-caption {
		flex: 1 1 16rem;
		@include inset-squish-md;
	}
	@supports (grid-area: x) {
		display: flex !important; // no grid - flex does it better
	}
}

/* Inline variant mixin - positions content and variant side-by-side
	with no preset image:caption ratio */
@mixin Media--inline {
	flex-direction: row;
	@supports (grid-area: x) {
		grid-template-columns: auto 1fr;
		grid-template-rows: auto;
		grid-template-areas: 'content caption';
	}
}
/* Inline variant class */
.Media--inline {
	@include Media--inline;
}
/* Inline variant at medium up class */
.Media--inline-md {
	@include breakpoint(medium) {
		@include Media--inline;
	}
}

/* ---------------- 
	Variants for inline media-to-caption size ratios
---------------- */

/* Size 1 of 4 mixin - content spans one fourth of total width */
@mixin Media--inline-1to3 {
	@include Media--inline;
	.Media-content {
		flex: 1 3 25%;
	}
	.Media-caption {
		flex: 3 1 75%;
	}
	@supports (grid-area: x) {
		grid-template-columns: 1fr 3fr;
	}
}

/* Size 1 of 3 mixin - content spans one third of total width */
@mixin Media--inline-1to2 {
	@include Media--inline;
	.Media-content {
		flex: 1 2 33%;
	}
	.Media-caption {
		flex: 2 1 66%;
	}
	@supports (grid-area: x) {
		grid-template-columns: 1fr 2fr;
	}
}

/* Size 1 of 2 mixin - content spans half of total width */
@mixin Media--inline-1to1 {
	@include Media--inline;
	.Media-content {
		flex: 1 1 50%;
	}
	.Media-caption {
		flex: 1 1 50%;
	}
	@supports (grid-area: x) {
		grid-template-columns: 1fr 1fr;
	}
}

/* Size 2 of 3 mixin - content spans two thirds of total width */
@mixin Media--inline-2to1 {
	@include Media--inline;
	.Media-content {
		flex: 2 1 66%;
	}
	.Media-caption {
		flex: 1 2 33%;
	}
	@supports (grid-area: x) {
		grid-template-columns: 2fr 1fr;
	}
}

/* Size 3 of 4 mixin - content spans three fourths of total width */
@mixin Media--inline-3to1 {
	@include Media--inline;
	.Media-content {
		flex: 3 1 75%;
	}
	.Media-caption {
		flex: 1 3 25%;
	}
	@supports (grid-area: x) {
		grid-template-columns: 3fr 1fr;
	}
}

/* Classes for inline media-to-caption ratios */
.Media--inline-1to3 {
	@include Media--inline-1to3;
}
.Media--inline-1to2 {
	@include Media--inline-1to2;
}
.Media--inline-1to1 {
	@include Media--inline-1to1;
}
.Media--inline-2to1 {
	@include Media--inline-2to1;
}
.Media--inline-3to1 {
	@include Media--inline-3to1;
}

/* Classes for ratios at medium up */
.Media--inline-md-1to3 {
	@include breakpoint(medium) {
		@include Media--inline-1to3;
	}
}
.Media--inline-md-1to2 {
	@include breakpoint(medium) {
		@include Media--inline-1to2;
	}
}
.Media--inline-md-1to1 {
	@include breakpoint(medium) {
		@include Media--inline-1to1;
	}
}
.Media--inline-md-2to1 {
	@include breakpoint(medium) {
		@include Media--inline-2to1;
	}
}
.Media--inline-md-3to1 {
	@include breakpoint(medium) {
		@include Media--inline-3to1;
	}
}

/* ---------------- 
	Variants for special things
---------------- */

/* Hero variant - for large featured images with smaller captions */
.Media--hero {
	margin: 0;
	flex-direction: column;
	&--transparent {
		background: transparent;
	}
	&-title {
		font-size: 2.25rem; // $font-size-7
		line-height: 1.2222; // $line-height-7-loose
		margin-bottom: $space-xs;
	}
	&-subTitle-0 {
		font-size: 0.88rem; // $font-size-3
		line-height: 1.4205; // $line-height-3
		color: $color-neutral-2;
		margin-bottom: $space-xs;
	}
	&-subTitle-1 {
		font-size: 1rem; // $font-size-4
		line-height: 1.5; // $line-height-4
		color: $color-neutral-2;
	}
	> .Media-content {
		margin: 0;
		> .Media {
			display: flex;
			@supports (grid-template-areas: 'x') {
				display: grid;
			}
		}
	}
	> .Media-caption {
		@include inset-md;
		flex-basis: auto;
		align-self: stretch;
	}
	@include breakpoint(medium) {
		flex-direction: row-reverse;
		@include breakpoint(medium) {
			&-title {
				margin-bottom: $space-sm;
			}
			&-subTitle-0 {
				margin-bottom: $space-sm;
			}
		}
		> .Media-content {
			align-self: center;
			flex-basis: 50%;
		}
		> .Media-caption {
			@include inset-lg;
			flex-basis: 50%;
			align-self: center;
			&--alignBottom {
				align-self: flex-end;
			}
		}
	}
	@include breakpoint(large) {
		&-title {
			font-size: 3.25rem; // $font-size-8
			line-height: 1; // $line-height-8
		}
	}
	@include breakpoint(xlarge) {
		> .Media-content {
			flex-basis: 66.666%;
		}
		> .Media-caption {
			flex-basis: 33.333%;
		}
	}
	@include breakpoint(xxlarge) {
		> .Media-caption {
			padding: 3rem; // cheating because $space-xl (4rem) is just too much
		}
	}

	// Grid layout for supporting browsers
	@supports (grid-template-areas: 'x') {
		grid-gap: 0;
		@include breakpoint(medium) {
			grid-template-columns: 1fr 1fr;
			grid-template-rows: auto;
			grid-template-areas: 'caption content';
		}
		@include breakpoint(xlarge) {
			grid-template-columns: 1fr 2fr;
		}
	}

	// Bug class - special for Science hero
	.Media-content-bug {
		display: flex;
		margin: 0;
		overflow: hidden;
		> div {
			flex: 1 1 50%;
		}
		> div:nth-child(2) {
			display: flex;
			.Panel {
				align-items: center;
				display: flex;
				justify-content: space-around;
				width: 100%;
			}
		}
		@include breakpoint(large) {
			@include dropShadow(2);
			border-radius: 3px;
			position: absolute;
			width: 30%;
			left: $space-sm;
			[dir='rtl'] & {
				left: auto;
				right: $space-sm;
			}
			bottom: $space-sm;
			display: block;
			> div {
				flex: 1 1 100%;
			}
		}
		@include breakpoint(xlarge) {
			left: $space-md;
			[dir='rtl'] & {
				left: auto;
				right: $space-md;
			}
			bottom: $space-md;
			width: 25%;
		}
	}
}

/* Mirror variant of Hero variant
	For positioning caption on the right at medium up */
.Media--hero--mirror {
	@include breakpoint(medium) {
		flex-direction: row;
	}
	// Grid layout for supporting browsers
	@supports (grid-template-areas: 'x') {
		@include breakpoint(medium) {
			grid-template-areas: 'content caption';
		}
		@include breakpoint(xlarge) {
			grid-template-columns: 2fr 1fr;
		}
	}
}

/* Flip variant of Hero variant
	for positioning caption above content on small only */
.Media--hero--flip {
	flex-direction: column-reverse;
	@include breakpoint(medium) {
		flex-direction: row-reverse;
		&.Media--hero--mirror {
			flex-direction: row;
		}
	}
	// Grid layout for supporting browsers
	@supports (grid-template-areas: 'x') {
		grid-template-areas:
			'caption'
			'content';
		@include breakpoint(medium) {
			grid-template-areas: 'caption content';
			&.Media--hero--mirror {
				grid-template-areas: 'content caption';
			}
		}
	}
}

/* Top rule mixin - for adding a light grey line above media item,
	useful for visually separating a list of media items */
@mixin Media--topRule {
	padding-top: 1rem;
	border-top: 1px solid $color-neutral-1;
}
/* Top rule variant class */
.Media--topRule {
	@include Media--topRule;
}
/* Top rule for small screens only variant class */
.Media--topRule-sm {
	@media (max-width: 768px) {
		@include Media--topRule;
	}
}

/* Special styles for a carousel (slides) inside Media-content */
.Media--slides {
	display: flex;
	> .Media-content {
		overflow: hidden;
		@include bg-neutral-dark;
		display: block; // override Media--hero
	}
	@include breakpoint(medium) {
		> .Media-content {
			flex-basis: 66.666%;
		}
		> .Media-caption {
			flex-basis: 33.333%;
		}
	}
	// Grid layout for supporting browsers
	@supports (grid-template-areas: 'x') {
		@include breakpoint(medium) {
			display: grid;
			grid-template-columns: 1fr 2fr;
		}
	}
}

.Media--overlayLeft {
	margin: 0;
	> .Media-caption {
		background-color: $color-neutral-5;
		@include inset-md;
		@include colorReverse;
	}
	@include breakpoint(medium) {
		display: flex;
		position: relative;
		> .Media-caption {
			background-color: $color-overlay-black;
			position: absolute;
			width: 35%;
			top: 50%;
			left: $space-lg;
			[dir='rtl'] & {
				left: auto;
				right: $space-lg;
			}
			transform: translateY(-50%);
			z-index: 10;
			border-radius: $radius-sm;
			@include breakpoint(large) {
				left: $space-xl;
				[dir='rtl'] & {
					left: auto;
					right: $space-xl;
				}
				@include inset-lg;
				width: 33.333%;
			}
			@include breakpoint(xlarge) {
				width: 33.333%;
			}
		}
	}
}

.Media--overlayLeftWithoutBackground {
	margin: 0;
	> .Media-caption {
		background-color: $color-neutral-5;
		@include inset-md;
		@include colorReverse;
		.Media-caption-identity {
			position: absolute;
			top: $space-lg;
			left: $space-lg;
			[dir='rtl'] & {
				left: auto;
				right: $space-lg;
			}
		}
	}
	@include breakpoint(medium) {
		display: flex;
		position: relative;
		> .Media-caption {
			background-color: transparent;
			position: absolute;
			top: $space-xl;
			bottom: 0;
			left: $space-xl;
			[dir='rtl'] & {
				left: auto;
				right: $space-xl;
			}
			z-index: 10;
			padding: 0;
			display: flex;
			flex-direction: column;
			.Media-caption-identity {
				position: relative;
				top: 0;
				left: 0;
			}
			.Media-caption-inner {
				flex: 1 0 auto;
				display: flex;
				align-items: center;
				margin-left: $space-xl;
				[dir='rtl'] & {
					margin-left: 0;
					margin-right: $space-xl;
				}
			}
		}
	}
}

.Media--contained {
	height: 100%;
	grid-gap: $space-xs;
	margin: 0;
	.Media-content {
		align-self: center;
		text-align: center;
	}
	.Media-caption {
		align-self: flex-end;
		width: 100%; // needed for IE
	}
}

.Media--dropshadow {
	background-color: $color-white;
	@include dropshadow-1;
	@include inset-sm;
}

.Media--overlay {
	position: relative;
	display: block;
	margin: 0;
	.Media-content {
		margin: 0;
	}
	.Media-caption {
		position: absolute;
		bottom: 0;
		right: 0;
		left: 0;
		background-color: $color-overlay-black;
		@include inset-xs;
		@include colorReverse;
		@include text(2);
	}
}
</style>
